import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Route, Switch, Redirect } from 'react-router';
import { ConnectedRouter } from 'react-router-redux';
import { Provider, connect } from 'react-redux';
import ReactGA from 'react-ga';

import { ROUTE_PATH, APP_SIDER_MENU, PERMISSIONS } from './data/config/constants';
import { isModuleAccessible, isActionPermitted, log } from './data/config/utils';
import { BRAND_INFO } from './data/config/urls';

import PrivateRoute from './components/privateroute';
import NotFound from './components/notFound';
import App from './app';
import AuthContainer from './app/authcontainer';
import Login from './app/authcontainer/login';
import ForgotPassword from './app/authcontainer/forgotpassword';
import ResetPassword from './app/authcontainer/resetpassword';
import PageContainer from './app/pagecontainer';
import Chats from './app/pagecontainer/modules/chats';
import Dashboard from './app/pagecontainer/modules/dashboard';
import ChatHistory from './app/pagecontainer/modules/history';
import UsageAnalytics from './app/pagecontainer/modules/analytics/usageanalytics';
import ChatlogAnalytics from './app/pagecontainer/modules/analytics/chatloganalytics';
import EventAnalytics from './app/pagecontainer/modules/analytics/eventanalytics';
import AgentMonitoring from './app/pagecontainer/modules/analytics/agentmonitoring';
import SessionFlow from './app/pagecontainer/modules/analytics/sessionflow';
import Conversions from './app/pagecontainer/modules/analytics/conversions';
import Feedback from './app/pagecontainer/modules/analytics/feedback';
import BotBuilder from './app/pagecontainer/modules/botbuilder';
import Workflows from './app/pagecontainer/modules/botbuilder/workflows';
import WorkflowDiagram from './app/pagecontainer/modules/botbuilder/workflows/workflowdiagram';
import BotMessages from './app/pagecontainer/modules/botbuilder/botmessages';
import BrandFunctions from './app/pagecontainer/modules/botbuilder/brandfunctions';
import EditBrandFunction from './app/pagecontainer/modules/botbuilder/brandfunctions/EditBrandFunction';
import Events from './app/pagecontainer/modules/botbuilder/events';
import BotParams from './app/pagecontainer/modules/botbuilder/botparams';
import Intents from './app/pagecontainer/modules/botbuilder/intents';
import EditIntent from './app/pagecontainer/modules/botbuilder/intents/editintent';
import Entities from './app/pagecontainer/modules/botbuilder/entities';
import EditEntity from './app/pagecontainer/modules/botbuilder/entities/editentity';
import Users from './app/pagecontainer/modules/users';
import UserProfile from './app/pagecontainer/modules/users/userprofile';
import Settings from './app/pagecontainer/modules/settings';
import Downtime from './app/pagecontainer/modules/settings/downtime';
import ProfileSetting from './app/pagecontainer/modules/settings/profilesetting';
import TeamMemberSetting from './app/pagecontainer/modules/settings/teammembersetting';
import MemberPermissions from './app/pagecontainer/modules/settings/teammembersetting/memberpermissions';
import Members from './app/pagecontainer/modules/members';
import Agents from './app/pagecontainer/modules/members/agents';
import EditAgentProfile from './app/pagecontainer/modules/members/agents/editagentprofile';
import Groups from './app/pagecontainer/modules/members/groups';
import AddAndEditGroup from './app/pagecontainer/modules/members/groups/addandeditgroup';
import CannedResponses from './app/pagecontainer/modules/members/cannedresponses';
import Tags from './app/pagecontainer/modules/members/tags';
import Kpi from './app/pagecontainer/modules/analytics/kpi';
import QueryAnalytics from './app/pagecontainer/modules/analytics/queryanalytics';
import ModelAccuracy from './app/pagecontainer/modules/analytics/modelaccuracy';
import APILogs from './app/pagecontainer/modules/console/apilogs';
import Reports from './app/pagecontainer/modules/console/reports';
import Fracture from './app/pagecontainer/modules/analytics/Fracture';
import EmailSendBox from './app/pagecontainer/modules/emailsendbox'

ReactGA.initialize("ga-0008-your-id");

const logPageView = () => {
  ReactGA.pageview(window.location.pathname + window.location.search);
};

class Root extends Component {
  redirectToAccessibleChild = parent_path => {
    const { permissions } = this.props;
    const children = APP_SIDER_MENU.find(item => { return (item.route_path === parent_path); }).children;
    let accessibleChildPath = children[0].route_path;
    let index = children.findIndex(child => { return (isModuleAccessible(permissions, child.route_path, parent_path)); });
    if (index !== -1) {
      accessibleChildPath = children[index].route_path;
    }
    return accessibleChildPath;
  };

  render() {
    const { store, history, permissions } = this.props;
    log('root rendered')
    return (
      <Provider store={store}>
        <ConnectedRouter history={history} onChange={logPageView}>
          <App>
            <Switch>
              <Redirect exact from={ROUTE_PATH.AUTH} to={ROUTE_PATH.LOGIN} />

              <Route path={ROUTE_PATH.AUTH}>
                <AuthContainer>
                  <Switch>
                    <Route exact path={ROUTE_PATH.LOGIN} component={Login} />
                    <Route exact path={ROUTE_PATH.FORGOT_PASSWORD} component={ForgotPassword} />
                    <Route exact path={ROUTE_PATH.RESET_PASSWORD} component={ResetPassword} />
                    <Route exact path={ROUTE_PATH.ACTIVATE_USER} component={ResetPassword} />
                  </Switch>
                </AuthContainer>
              </Route>

              <PageContainer>
                <Switch>
                  <Redirect exact from={ROUTE_PATH.ANALYTICS} to={this.redirectToAccessibleChild(ROUTE_PATH.ANALYTICS)} />
                  <Redirect exact from={ROUTE_PATH.BOT_BUILDER} to={this.redirectToAccessibleChild(ROUTE_PATH.BOT_BUILDER)} />
                  <Redirect exact from={ROUTE_PATH.MEMBERS} to={this.redirectToAccessibleChild(ROUTE_PATH.MEMBERS)} />
                  <Redirect exact from={ROUTE_PATH.SETTINGS} to={this.redirectToAccessibleChild(ROUTE_PATH.SETTINGS)} />
                  <Redirect exact from={ROUTE_PATH.CONSOLE} to={this.redirectToAccessibleChild(ROUTE_PATH.CONSOLE)} />

                  <PrivateRoute exact path={ROUTE_PATH.DASHBOARD} component={BRAND_INFO.usage_home_page ? UsageAnalytics : Dashboard} />

                  <PrivateRoute exact parent_path={ROUTE_PATH.ANALYTICS} path={ROUTE_PATH.USAGE_ANALYTICS} component={BRAND_INFO.usage_home_page ? Dashboard : UsageAnalytics} />
                  <PrivateRoute exact parent_path={ROUTE_PATH.ANALYTICS} path={ROUTE_PATH.CHATLOG_ANALYTICS} component={ChatlogAnalytics} />
                  <PrivateRoute exact is_permitted parent_path={ROUTE_PATH.ANALYTICS} path={ROUTE_PATH.EVENT_ANALYTICS} component={EventAnalytics} />
                  <PrivateRoute exact is_permitted parent_path={ROUTE_PATH.ANALYTICS} path={ROUTE_PATH.AGENT_MONITORING} component={AgentMonitoring} />
                  <PrivateRoute exact parent_path={ROUTE_PATH.ANALYTICS} path={ROUTE_PATH.CONVERSIONS} component={Conversions} />
                  <PrivateRoute
                    path={ROUTE_PATH.FRACTURE}
                    parent_path={ROUTE_PATH.ANALYTICS}
                    component={Fracture}
                  />
                  <PrivateRoute exact parent_path={ROUTE_PATH.ANALYTICS} path={ROUTE_PATH.SESSION_FLOW} component={SessionFlow} />
                  <PrivateRoute exact parent_path={ROUTE_PATH.ANALYTICS} path={ROUTE_PATH.FEEDBACK} component={Feedback} />
                  <PrivateRoute exact parent_path={ROUTE_PATH.ANALYTICS} path={ROUTE_PATH.KPI} component={Kpi} />
                  <PrivateRoute exact parent_path={ROUTE_PATH.ANALYTICS} path={ROUTE_PATH.QUERY_ANALYTICS} component={QueryAnalytics} />
                  <PrivateRoute exact parent_path={ROUTE_PATH.ANALYTICS} path={ROUTE_PATH.MODEL_ACCURACY} component={ModelAccuracy} />
                  <PrivateRoute exact parent_path={ROUTE_PATH.CONSOLE} path={ROUTE_PATH.API_LOGS} component={APILogs} />
                  <PrivateRoute exact parent_path={ROUTE_PATH.CONSOLE} path={ROUTE_PATH.REPORTS} component={Reports} />

                  <PrivateRoute exact path={ROUTE_PATH.CHATS} component={Chats} />
                  <PrivateRoute exact path={ROUTE_PATH.CHAT_HISTORY} component={ChatHistory} />
                  <PrivateRoute exact path={ROUTE_PATH.EMAIL_SEND_BOX} component={EmailSendBox} />

                  <PrivateRoute exact path={ROUTE_PATH.USERS} component={Users} />
                  <PrivateRoute is_permitted={isModuleAccessible(permissions, ROUTE_PATH.USERS)} path={ROUTE_PATH.USER_PROFILE} component={UserProfile} />

                  <PrivateRoute exact is_permitted={isModuleAccessible(permissions, ROUTE_PATH.WORKFLOWS, ROUTE_PATH.BOT_BUILDER)} path={ROUTE_PATH.WORKFLOW_DIAGRAM} component={WorkflowDiagram} />
                  <PrivateRoute exact is_permitted={isModuleAccessible(permissions, ROUTE_PATH.INTENTS, ROUTE_PATH.BOT_BUILDER) && isActionPermitted(permissions, PERMISSIONS.ACTIONS.INTENT, PERMISSIONS.ACTION_TYPES.UPDATE)} path={ROUTE_PATH.EDIT_INTENT} component={EditIntent} />
                  <PrivateRoute exact is_permitted={isModuleAccessible(permissions, ROUTE_PATH.ENTITIES, ROUTE_PATH.BOT_BUILDER) && isActionPermitted(permissions, PERMISSIONS.ACTIONS.ENTITY, PERMISSIONS.ACTION_TYPES.UPDATE)} path={ROUTE_PATH.EDIT_ENTITY} component={EditEntity} />
                  <PrivateRoute exact is_permitted={isModuleAccessible(permissions, ROUTE_PATH.BRAND_FUNCTIONS, ROUTE_PATH.BOT_BUILDER)} path={ROUTE_PATH.EDIT_BRAND_FUNCTION} component={EditBrandFunction} />
                  <Route path={ROUTE_PATH.BOT_BUILDER}>
                    <BotBuilder>
                      <Switch>
                        <PrivateRoute exact parent_path={ROUTE_PATH.BOT_BUILDER} path={ROUTE_PATH.WORKFLOWS} component={Workflows} />
                        <PrivateRoute exact parent_path={ROUTE_PATH.BOT_BUILDER} path={ROUTE_PATH.INTENTS} component={Intents} />
                        <PrivateRoute exact parent_path={ROUTE_PATH.BOT_BUILDER} path={ROUTE_PATH.ENTITIES} component={Entities} />
                        <PrivateRoute exact parent_path={ROUTE_PATH.BOT_BUILDER} path={ROUTE_PATH.BOT_MESSAGES} component={BotMessages} />
                        <PrivateRoute exact parent_path={ROUTE_PATH.BOT_BUILDER} path={ROUTE_PATH.BRAND_FUNCTIONS} component={BrandFunctions} />
                        <PrivateRoute exact parent_path={ROUTE_PATH.BOT_BUILDER} path={ROUTE_PATH.EVENTS} component={Events} />
                        <PrivateRoute exact parent_path={ROUTE_PATH.BOT_BUILDER} path={ROUTE_PATH.BOT_PARAMS} component={BotParams} />
                        <Route component={NotFound} />
                      </Switch>
                    </BotBuilder>
                  </Route>

                  <PrivateRoute is_permitted={isModuleAccessible(permissions, ROUTE_PATH.AGENTS, ROUTE_PATH.MEMBERS)} path={ROUTE_PATH.EDIT_AGENT_PROFILE} component={EditAgentProfile} />
                  <PrivateRoute is_permitted={isModuleAccessible(permissions, ROUTE_PATH.GROUPS, ROUTE_PATH.MEMBERS)} path={ROUTE_PATH.ADD_GROUP} component={AddAndEditGroup} />
                  <PrivateRoute is_permitted={isModuleAccessible(permissions, ROUTE_PATH.GROUPS, ROUTE_PATH.MEMBERS)} path={ROUTE_PATH.EDIT_GROUP} component={AddAndEditGroup} />
                  <Route path={ROUTE_PATH.MEMBERS}>
                    <Members>
                      <Switch>
                        <PrivateRoute exact parent_path={ROUTE_PATH.MEMBERS} path={ROUTE_PATH.TAGS} component={Tags} />
                        <PrivateRoute exact parent_path={ROUTE_PATH.MEMBERS} path={ROUTE_PATH.AGENTS} component={Agents} />
                        <PrivateRoute exact parent_path={ROUTE_PATH.MEMBERS} path={ROUTE_PATH.GROUPS} component={Groups} />
                        <PrivateRoute exact parent_path={ROUTE_PATH.MEMBERS} path={ROUTE_PATH.CANNED_RESPOSES} component={CannedResponses} />
                        <Route component={NotFound} />
                      </Switch>
                    </Members>
                  </Route>

                  <PrivateRoute is_permitted={isModuleAccessible(permissions, ROUTE_PATH.TEAM_MEMBER_SETTING, ROUTE_PATH.SETTINGS)} path={ROUTE_PATH.MEMBER_PERMISSIONS} component={MemberPermissions} />
                  <Route path={ROUTE_PATH.SETTINGS}>
                    <Settings>
                      <Switch>
                        <PrivateRoute exact parent_path={ROUTE_PATH.SETTINGS} path={ROUTE_PATH.DOWNTIME} component={Downtime} />
                        <PrivateRoute exact parent_path={ROUTE_PATH.SETTINGS} path={ROUTE_PATH.PROFILE_SETTING} component={ProfileSetting} />
                        <PrivateRoute exact parent_path={ROUTE_PATH.SETTINGS} path={ROUTE_PATH.TEAM_MEMBER_SETTING} component={TeamMemberSetting} />
                        <Route component={NotFound} />
                      </Switch>
                    </Settings>
                  </Route>
                  <Route component={NotFound} />
                </Switch>
              </PageContainer>
            </Switch>
          </App>
        </ConnectedRouter>
      </Provider>
    );
  }
}

const mapStateToProps = state => {
  return {
    permissions: state.admin_details.user_info && state.admin_details.user_info.permissions ? state.admin_details.user_info.permissions : null
  };
}

Root.propTypes = {
  store: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  permissions: PropTypes.object,
};

export default connect(mapStateToProps)(Root);



// WEBPACK FOOTER //
// ./src/root.js